import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuthStore } from '../stores/useAuthStore.js';
import VideoPlayer from '../components/VideoPlayer.js';
import Card from '../components/Card.js';
import { computeMasteryBadges } from '../lib/badges.js';
import { useToast } from '../hooks/useToast.js';

const RARITY_COLORS: Record<string, string> = {
  common: 'text-[#9e9e9e] border-[#9e9e9e]/30',
  rare: 'text-[#60a5fa] border-[#60a5fa]/30',
  epic: 'text-[#a78bfa] border-[#a78bfa]/30',
  legendary: 'text-[#ffbf00] border-[#ffbf00]/30'
};

const RARITY_BG: Record<string, string> = {
  common: 'bg-[rgba(158,158,158,0.08)]',
  rare: 'bg-[rgba(96,165,250,0.08)]',
  epic: 'bg-[rgba(167,139,250,0.08)]',
  legendary: 'bg-[rgba(255,191,0,0.08)]'
};

type ProfileTab = 'stats' | 'inventory' | 'clips';

export default function UserProfile() {
  const { username } = useParams();
  const { user: currentUser, setUser: setCurrentUser } = useAuthStore();

  const [profileUser, setProfileUser] = useState<any>(null);
  const [stats, setStats] = useState<any>({});
  const [clips, setClips] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<ProfileTab>('stats');

  const [inventory, setInventory] = useState<any>(null);
  const [equipping, setEquipping] = useState<string | null>(null);
  const [equippedCosmetics, setEquippedCosmetics] = useState<any>({});
  const [previewCosmeticId, setPreviewCosmeticId] = useState<string | null>(null);

  const [isEditingBio, setIsEditingBio] = useState(false);
  const [editBio, setEditBio] = useState('');
  const [isUpdatingBio, setIsUpdatingBio] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [accentColor, setAccentColor] = useState<string>('');
  const [isSavingAccent, setIsSavingAccent] = useState(false);

  const [activeModalClip, setActiveModalClip] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const toast = useToast();
  const isMe = currentUser?.username === username;

  useEffect(() => {
    async function fetchProfileData() {
      setIsLoading(true);
      try {
        const userRes = await api.get(`/api/users/${username}`);
        setProfileUser(userRes.data);
        setEditBio(userRes.data?.bio || '');
        setAccentColor(userRes.data?.accent_color || '');

        const statsRes = await api.get(`/api/users/${username}/stats`);
        setStats(statsRes.data || {});

        const clipsRes = await api.get(`/api/users/${username}/clips`);
        setClips(clipsRes.data || []);

        const cosmeticsRes = await api.get(`/api/cosmetics/user/${username}`);
        setEquippedCosmetics(cosmeticsRes.data || {});

        if (currentUser?.username === username) {
          const inventoryRes = await api.get('/api/cosmetics/me');
          setInventory(inventoryRes.data);
        }
      } catch (err) {
        console.error('Failed to load profile details:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchProfileData();
  }, [username, currentUser?.username]);

  const showToast = (type: 'success' | 'error', message: string) => void toast.push({ type, message });

  const handleSaveBio = async () => {
    if (!isMe) return;
    setIsUpdatingBio(true);
    setError(null);
    try {
      const response = await api.put('/api/users/me', { bio: editBio });
      const updatedUser = response.data;
      setProfileUser(updatedUser);
      setCurrentUser({ ...currentUser!, bio: updatedUser.bio, avatar_url: updatedUser.avatar_url, banner_url: updatedUser.banner_url, accent_color: updatedUser.accent_color });
      setIsEditingBio(false);
      showToast('success', 'Profile saved');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save bio.');
      showToast('error', err.response?.data?.message || 'Failed to save bio.');
    } finally {
      setIsUpdatingBio(false);
    }
  };

  const PRESET_COLORS = useMemo(
    () => ['#e03c3c', '#ffbf00', '#4caf50', '#60a5fa', '#a78bfa', '#22c55e', '#f97316', '#f472b6'],
    []
  );

  const handleSaveAccent = async (color: string) => {
    if (!isMe) return;
    setIsSavingAccent(true);
    try {
      const response = await api.put('/api/users/me', { accent_color: color });
      const updatedUser = response.data;
      setProfileUser(updatedUser);
      setAccentColor(updatedUser.accent_color || '');
      setCurrentUser({ ...currentUser!, bio: updatedUser.bio, avatar_url: updatedUser.avatar_url, banner_url: updatedUser.banner_url, accent_color: updatedUser.accent_color });
      showToast('success', 'Profile saved');
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to save accent color.');
    } finally {
      setIsSavingAccent(false);
    }
  };

  const handleEquip = async (cosmeticId: string) => {
    setEquipping(cosmeticId);
    try {
      const res = await api.post(`/api/cosmetics/equip/${cosmeticId}`);
      showToast('success', `Equipped ${res.data.equipped.name}!`);
      const inventoryRes = await api.get('/api/cosmetics/me');
      setInventory(inventoryRes.data);
      const cosmeticsRes = await api.get(`/api/cosmetics/user/${username}`);
      setEquippedCosmetics(cosmeticsRes.data);
    } catch (err: any) {
      showToast('error', err.response?.data?.message || 'Failed to equip cosmetic');
    } finally {
      setEquipping(null);
    }
  };

  const handleUnequip = async (type: string) => {
    try {
      await api.post(`/api/cosmetics/unequip/${type}`);
      showToast('success', 'Unequipped');
      const inventoryRes = await api.get('/api/cosmetics/me');
      setInventory(inventoryRes.data);
      const cosmeticsRes = await api.get(`/api/cosmetics/user/${username}`);
      setEquippedCosmetics(cosmeticsRes.data);
    } catch (err: any) {
      showToast('error', 'Failed to unequip');
    }
  };

  const handlePreview = (cosmeticId: string) => setPreviewCosmeticId(cosmeticId);
  const exitPreview = () => setPreviewCosmeticId(null);

  const getGamemodeColor = (gm: string) => {
    switch (gm) {
      case 'mace': case 'mace_rockets': return 'bg-[rgba(255,191,0,0.15)] text-[#ffbf00]';
      case 'sword': case 'cpvp': return 'bg-[rgba(224,60,60,0.15)] text-[#e03c3c]';
      case 'axe_shield': return 'bg-[rgba(76,175,80,0.15)] text-[#4caf50]';
      default: return 'bg-[rgba(255,255,255,0.08)] text-on-surface-variant';
    }
  };

  const getGamemodeLabel = (gm: string) => {
    if (gm === 'cpvp') return 'Crystal PvP';
    if (gm === 'axe_shield') return 'Axe PvP';
    if (gm === 'mace_rockets') return 'Mace Rockets';
    return gm.charAt(0).toUpperCase() + gm.slice(1);
  };

  if (isLoading) {
    return (
      <div className="flex-1 min-h-screen bg-background flex items-center justify-center text-on-surface-variant font-medium">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-error border-t-transparent rounded-full animate-spin"></div>
          <span>Loading profile...</span>
        </div>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="flex-1 min-h-screen bg-background flex items-center justify-center text-on-surface-variant select-none">
        <div className="text-center bg-[#141414] thin-border p-gutter rounded-lg">
          <span className="material-symbols-outlined text-[48px] text-error mb-2">person_off</span>
          <p>User "{username}" could not be found.</p>
        </div>
      </div>
    );
  }

  const previewCosmetic = previewCosmeticId
    ? (inventory?.all || []).find((c: any) => c.id === previewCosmeticId)
    : null;

  const avatarSrc =
    (previewCosmetic?.type === 'avatar' && previewCosmetic?.imageUrl) ||
    equippedCosmetics?.equippedAvatar?.imageUrl ||
    profileUser.avatar_url ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(profileUser.username)}&background=1a1a1a&color=e03c3c&size=120&bold=true&format=svg`;

  const bannerSrc =
    (previewCosmetic?.type === 'banner' && previewCosmetic?.imageUrl) ||
    equippedCosmetics?.equippedBanner?.imageUrl ||
    profileUser.banner_url ||
    '';

  const masteryBadges = computeMasteryBadges(stats);
  const totalLikes = clips.reduce((sum, c) => sum + (c.likeCount || 0), 0);
  const borderStyle = profileUser.accent_color ? { borderColor: profileUser.accent_color } : undefined;

  const ownedIds = new Set((inventory?.owned || []).map((c: any) => c.id));
  const avatarCosmetics = (inventory?.all || []).filter((c: any) => c.type === 'avatar');
  const bannerCosmetics = (inventory?.all || []).filter((c: any) => c.type === 'banner');

  return (
    <div className="flex-1 min-h-screen bg-background text-on-surface select-none">
      <div className="bg-[#141414] thin-border rounded-lg p-0 overflow-hidden mb-8" style={borderStyle}>
        <div className="h-40 bg-surface-variant relative">
          {bannerSrc ? (
            <img alt="Profile banner" src={bannerSrc} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-[#141414] to-surface-variant" />
          )}
        </div>

        <div className="p-gutter">
          <div className="flex flex-col md:flex-row gap-gutter items-center md:items-start text-center md:text-left">
            <div className="relative w-24 h-24 flex-shrink-0">
              <div className="w-full h-full rounded-full overflow-hidden bg-surface-variant">
                <img
                  alt="Profile Avatar"
                  className="w-full h-full object-cover"
                  src={avatarSrc}
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    img.onerror = null;
                    img.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(profileUser.username)}&background=1a1a1a&color=e03c3c&size=120&bold=true&format=svg`;
                  }}
                />
              </div>
            </div>

            <div className="flex-1 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h1 className="font-headline-lg text-headline-lg text-on-surface leading-none">
                    {profileUser.username}
                  </h1>
                  <span className={`text-[11px] uppercase tracking-widest font-bold block mt-1 ${
                    profileUser.is_admin 
                      ? 'text-[#00ff00] font-["Share_Tech"] text-shadow-lg' 
                      : 'text-error'
                  }`}>
                    {profileUser.is_admin ? 'ADMIN' : profileUser.rank_title || 'Fresh Spawn'} ({profileUser.total_xp || 0} XP)
                  </span>
                </div>
                {previewCosmeticId && isMe && (
                  <button
                    onClick={exitPreview}
                    className="text-xs px-2 py-1 bg-surface-container rounded font-semibold text-on-surface-variant hover:text-on-surface transition-colors mt-1"
                  >
                    Exit Preview
                  </button>
                )}
              </div>

              {error && <div className="text-[12px] text-error font-semibold">{error}</div>}

              {isMe ? (
                <div className="max-w-xl">
                  {!isEditingBio ? (
                    <p
                      role="button"
                      tabIndex={0}
                      onClick={() => setIsEditingBio(true)}
                      onKeyDown={(e) => e.key === 'Enter' && setIsEditingBio(true)}
                      className="text-on-surface-variant text-body-sm leading-relaxed cursor-text hover:text-on-surface transition-colors"
                      title="Click to edit bio"
                    >
                      {profileUser.bio || 'Click to add a bio…'}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <textarea
                        rows={2}
                        value={editBio}
                        maxLength={160}
                        onChange={(e) => setEditBio(e.target.value)}
                        className="w-full bg-[#1a1a1a] border border-white/10 rounded-[4px] px-3 py-2 text-on-surface outline-none focus:border-error text-[13px] resize-none"
                      />
                      <div className="flex justify-between items-center text-[11px] text-on-surface-variant">
                        <span>{editBio.length} / 160</span>
                        <div className="flex gap-2">
                          <button type="button" onClick={handleSaveBio} disabled={isUpdatingBio} className="px-3 py-1.5 bg-error text-white text-xs font-bold rounded-[4px] disabled:opacity-50">
                            {isUpdatingBio ? 'Saving…' : 'Save'}
                          </button>
                          <button type="button" onClick={() => { setIsEditingBio(false); setEditBio(profileUser.bio || ''); setError(null); }} className="px-3 py-1.5 border border-white/10 text-xs rounded-[4px] text-on-surface-variant hover:text-on-surface">
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-on-surface-variant text-body-sm leading-relaxed max-w-xl">
                  {profileUser.bio || 'This user has not written a bio yet.'}
                </p>
              )}

              {isMe && (
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-on-surface-variant font-bold mb-2">Accent color</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {PRESET_COLORS.map((c) => {
                      const selected = (accentColor || '').toLowerCase() === c.toLowerCase();
                      return (
                        <button key={c} type="button" disabled={isSavingAccent} onClick={() => { setAccentColor(c); void handleSaveAccent(c); }} className={`w-8 h-8 rounded-full border transition-transform active:scale-95 ${selected ? 'border-white' : 'border-white/10'}`} style={{ backgroundColor: c }} title={c} />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-stack-md mb-stack-lg">
        {[
          { label: 'Total XP', value: profileUser.total_xp ?? 0 },
          { label: 'Rank', value: profileUser.is_admin ? 'ADMIN' : profileUser.rank_title ?? 'Fresh Spawn', isAdmin: profileUser.is_admin },
          { label: 'Clips', value: clips.length },
          { label: 'Likes', value: totalLikes }
        ].map((item) => (
          <Card key={item.label} className="p-4 text-center">
            <div className={`font-headline-sm text-headline-sm ${item.isAdmin ? 'text-[#00ff00] font-["Share_Tech"]' : 'text-on-surface'}`}>{item.value}</div>
            <div className="font-label-caps text-[10px] text-on-surface-variant uppercase mt-1">{item.label}</div>
          </Card>
        ))}
      </div>

      <div className="flex gap-1 mb-6 border-b border-outline-variant/40 pb-0">
        {([
          { id: 'stats', label: 'Combat Stats', icon: 'bar_chart' },
          ...(isMe ? [{ id: 'inventory', label: 'Inventory', icon: 'inventory_2' }] : []),
          { id: 'clips', label: 'Clips', icon: 'movie' }
        ] as { id: ProfileTab; label: string; icon: string }[]).map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-button transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-error text-on-surface'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'stats' && (
        <>
          <section className="mb-stack-lg">
            <div className="flex items-center justify-between mb-4 px-2">
              <h3 className="font-headline-sm text-on-surface uppercase tracking-widest text-xs opacity-60">Earned Mastery Badges</h3>
            </div>
            <div className="flex flex-wrap gap-4 px-2">
              {masteryBadges.map((badge) => (
                <div key={badge.id} className={`flex flex-col items-center gap-2 group cursor-help ${!badge.earned && badge.id !== 'locked' ? 'opacity-40 grayscale' : ''}`} title={badge.earned ? badge.label : 'Complete all lessons in this gamemode to unlock'}>
                  <div className={`w-16 h-16 rounded-full bg-surface-container-high flex items-center justify-center thin-border transition-colors ${badge.hoverBorder}`}>
                    <span className={`material-symbols-outlined text-3xl ${badge.color}`} style={{ fontVariationSettings: badge.earned ? "'FILL' 1" : "'FILL' 0" }}>{badge.icon}</span>
                  </div>
                  <span className="font-label-caps text-[10px] text-on-surface-variant">{badge.label}</span>
                </div>
              ))}
            </div>
          </section>

          <h2 className="font-label-caps text-label-caps text-on-surface-variant uppercase mb-4 tracking-widest">Combat Drills stats</h2>
          <div className="bg-[#141414] thin-border rounded-lg p-gutter space-y-6">
            {Object.keys(stats).length > 0 ? (
              Object.keys(stats).map((gm) => {
                const gmData = stats[gm];
                return (
                  <div key={gm} className="space-y-2">
                    <div className="flex justify-between items-center text-body-sm select-none">
                      <span className={`px-2 py-0.5 rounded-[4px] text-[10px] font-bold uppercase ${getGamemodeColor(gm)}`}>{getGamemodeLabel(gm)}</span>
                      <span className="text-[12px] text-on-surface-variant font-mono">{gmData.completed} / {gmData.total} lessons ({gmData.percentage}%)</span>
                    </div>
                    <div className="w-full h-1.5 bg-surface-container rounded-full overflow-hidden">
                      <div className="h-full bg-error rounded-full transition-all duration-500" style={{ width: `${gmData.percentage}%` }}></div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-on-surface-variant">No completion metrics recorded yet.</div>
            )}
          </div>
        </>
      )}

      {activeTab === 'inventory' && isMe && (
        <div className="space-y-8">
          {[
            { label: 'Avatars', type: 'avatar', items: avatarCosmetics, equippedId: inventory?.equippedAvatarId },
            { label: 'Banners', type: 'banner', items: bannerCosmetics, equippedId: inventory?.equippedBannerId }
          ].map((section) => (
            <div key={section.type}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-label-caps text-label-caps text-on-surface-variant uppercase tracking-widest">{section.label}</h2>
                {section.equippedId && (
                  <button onClick={() => handleUnequip(section.type)} className="text-xs text-on-surface-variant hover:text-error transition-colors">
                    Unequip
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {section.items.map((cosmetic: any) => {
                  const owned = ownedIds.has(cosmetic.id);
                  const equipped = section.equippedId === cosmetic.id;
                  const rarityColor = RARITY_COLORS[cosmetic.rarity] || RARITY_COLORS.common;
                  const rarityBg = RARITY_BG[cosmetic.rarity] || RARITY_BG.common;

                  return (
                    <div
                      key={cosmetic.id}
                      className={`relative rounded-lg border p-3 flex flex-col items-center gap-2 transition-all ${
                        equipped
                          ? `${rarityColor} ${rarityBg} shadow-lg`
                          : owned
                          ? 'border-outline-variant bg-[#141414] hover:border-white/20'
                          : 'border-outline-variant/30 bg-[#0f1112] opacity-50 grayscale'
                      }`}
                    >
                      {equipped && (
                        <div className={`absolute top-2 right-2 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${rarityBg} ${rarityColor} border ${rarityColor}`}>
                          Equipped
                        </div>
                      )}
                      {!owned && (
                        <div className="absolute top-2 right-2">
                          <span className="material-symbols-outlined text-[16px] text-on-surface-variant">lock</span>
                        </div>
                      )}
                      <div className={`w-16 h-16 overflow-hidden flex-shrink-0 ${section.type === 'avatar' ? 'rounded-full' : 'rounded-lg'}`}>
                        <img src={cosmetic.imageUrl} alt={cosmetic.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="text-center">
                        <div className="text-xs font-semibold text-on-surface truncate max-w-[100px]">{cosmetic.name}</div>
                        <div className={`text-[10px] font-bold uppercase ${rarityColor}`}>{cosmetic.rarity}</div>
                      </div>
                      {!owned && (
                        <div className="text-[10px] text-on-surface-variant text-center leading-tight">{cosmetic.unlockCondition}</div>
                      )}
                      {owned && !equipped && (
                        <button
                          onClick={() => handleEquip(cosmetic.id)}
                          disabled={equipping === cosmetic.id}
                          className="w-full text-xs px-2 py-1.5 bg-error text-white rounded font-bold disabled:opacity-50 active:scale-[0.97]"
                        >
                          {equipping === cosmetic.id ? '...' : 'Equip'}
                        </button>
                      )}
                      {!owned && (
                        <button
                          onClick={() => handlePreview(cosmetic.id)}
                          className="w-full text-xs px-2 py-1.5 bg-surface-container border border-outline-variant rounded font-bold text-on-surface-variant hover:text-on-surface active:scale-[0.97]"
                        >
                          Preview
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'clips' && (
        <div className="space-y-4">
          {clips.length > 0 ? (
            clips.map((clip) => (
              <div key={clip.id} onClick={() => setActiveModalClip(clip)} className="flex gap-4 p-3 bg-[#141414] thin-border hover:border-white/20 transition-all rounded-lg cursor-pointer min-w-0">
                <div className="w-28 aspect-video bg-black rounded overflow-hidden flex-shrink-0 relative">
                  <img alt={clip.title} className="w-full h-full object-cover opacity-80" src={clip.thumbnailUrl} />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <span className="material-symbols-outlined text-white text-xl">play_circle</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5 select-none">
                  <h4 className="font-semibold text-on-surface text-body-sm truncate">{clip.title}</h4>
                  <div className="flex justify-between items-center text-[11px] text-on-surface-variant">
                    <span className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded uppercase text-[9px] font-bold">{clip.gamemode}</span>
                    <span>{clip.likeCount} likes</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-[#141414] thin-border rounded-lg p-stack-lg text-center text-on-surface-variant select-none">No clips posted by this user.</div>
          )}
        </div>
      )}

      {activeModalClip && (
        <div onClick={() => setActiveModalClip(null)} className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl bg-[#0d0d0d] thin-border rounded-lg overflow-hidden">
            <div className="p-4 border-b border-outline-variant flex justify-between items-center bg-surface-container select-none">
              <div>
                <h3 className="font-headline-sm text-body-lg text-on-surface truncate">{activeModalClip.title}</h3>
                <span className="text-[11px] text-on-surface-variant">{activeModalClip.likeCount} Likes • {activeModalClip.viewCount} Views</span>
              </div>
              <button onClick={() => setActiveModalClip(null)} className="text-on-surface-variant hover:text-on-surface">
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
            </div>
            <div className="p-4 bg-black">
              <VideoPlayer src={activeModalClip.videoUrl} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}